import React from 'react';
import { withTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { Promise } from 'bluebird';

import { constant, util } from '@helper';
import {
  Button,
  FlexboxGrid,
  Heading,
  HeadingGroup,
  Message,
  Panel,
  Stack,
  Text,
  toaster,
  Uploader,
  VStack,
} from 'rsuite';

import { Icon } from '@rsuite/icons';
import { FaArrowRight, FaMagic } from 'react-icons/fa';
import { FileType } from 'rsuite/esm/Uploader';
import { CVMakerAPI, EventTrackerAPI } from '@services/api';
import _ from 'lodash';
import AuthStorage from '@common/services/authStorage';
import { PrivateRoute } from '@common/components';

type State = {
  cvMakerType: 'build' | 'own' | '';
  step: number;
  files: FileType[];
};

const { withRouter } = util;

class CVMakerHomeScreen extends React.Component<any, State> {
  constructor(props: any) {
    super(props);

    this.state = {
      cvMakerType: '',
      step: 1,
      files: [],
    };
  }

  componentDidMount(): void {
    // eslint-disable-next-line global-require
    require('./styles.scss');

    const { location } = this.props;
    if (location) {
      if (location.key !== null) {
        EventTrackerAPI.pushEvent(location.pathname);
      }
    }
  }

  componentDidUpdate(
    _prevProps: Readonly<any>,
    prevState: Readonly<State>,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _snapshot?: any,
  ): void {
    const { files: currFile } = this.state;

    if (prevState.files.length !== currFile.length && currFile.length > 0) {
      this.replaceIcon();
    }
  }

  replaceIcon = async (): Promise<void> => {
    const svgText = await fetch('/icon-file-pdf.svg').then((res) => res.text());

    document
      .querySelectorAll('.rs-uploader-file-item-icon-wrapper')
      .forEach((el) => {
        const defaultSvg = el.querySelector('svg');
        if (
          defaultSvg &&
          !el.classList.contains('rs-uploader-file-item-icon-custom')
        ) {
          defaultSvg.outerHTML = svgText;
          el.classList.add('rs-uploader-file-item-icon-custom');
        }
      });
  };

  onNextClicked = (): void => {
    const { cvMakerType } = this.state;
    if (cvMakerType === 'build') {
      this.setState({ step: 2 });
    } else {
      this.setState({ step: 3 });
    }
  };

  onCancelClicked = (): void => {
    const { files } = this.state;
    if (files.length === 0) {
      this.setState({ step: 1 });
    } else {
      this.setState({ files: [] });
    }
  };

  onEnhanceClicked = async (): Promise<void> => {
    try {
      const { step, files } = this.state;
      if (files.length < 1) {
        return;
      }

      // Set the page to "LOADING" page
      this.setState({ step: step + 1 });

      // @call api to parse the CV
      const selectedFile = _.get(files, '[0]');
      const formData = new FormData();
      formData.append('file', selectedFile.blobFile as File);

      const response = await CVMakerAPI.parseCV(formData);
      const data = _.get(response, 'data.data');

      const { navigate, location } = this.props;
      await EventTrackerAPI.pushEvent(location.pathname, 'parse-cv');

      navigate('/cv-maker/build', {
        state: {
          cvData: _.get(data, 'data'),
          personalizedSummary: _.get(data, 'personalizedSummary'),
        },
      });
    } catch (e: any) {
      toaster.push(util.buildErrorMessage(e.message));
    }
  };

  renderInprogressCVParsing({ t } = this.props): React.ReactNode {
    return (
      <FlexboxGrid
        className="cv-maker-container"
        justify="center"
        align="middle"
      >
        <VStack spacing={36} className="w-50">
          <Stack spacing={36} className="w-100">
            <Stack.Item className="w-100 tg-text-center">
              <HeadingGroup className="tg-cvmaker-heading">
                <Heading>
                  {t(constant.translation.cvMaker.parsingInProgressTitle)}
                </Heading>
                <Text muted>
                  {t(constant.translation.cvMaker.parsingInProgressSubtitle)}
                </Text>
              </HeadingGroup>
            </Stack.Item>
          </Stack>
          <Stack.Item alignSelf="center">
            <div role="status" aria-busy="true" className="bounce-wrap">
              <img
                className="bounce-img"
                src="bg-analyzing-cv-inprogress.png"
                alt="own-cvmaker-inprogress-parsing"
              />
              <span className="bounce-shadow" />
            </div>
          </Stack.Item>
        </VStack>
      </FlexboxGrid>
    );
  }

  renderCVUploader(): React.ReactNode {
    const { t } = this.props;
    const { files: filesState } = this.state;

    return (
      <FlexboxGrid
        className="cv-maker-container"
        justify="center"
        align="middle"
      >
        <VStack spacing={36} className="w-50">
          <Stack spacing={36} className="w-100">
            <Stack.Item className="w-100 tg-text-center">
              <HeadingGroup className="tg-cvmaker-heading">
                <Heading>{t(constant.translation.cvMaker.uploadTitle)}</Heading>
                <Text muted>
                  {t(constant.translation.cvMaker.uploadSubtitle)}
                </Text>
              </HeadingGroup>
            </Stack.Item>
          </Stack>
          <Stack spacing={36} className="w-100">
            <Stack.Item className="w-80">
              <Uploader
                fileList={filesState}
                multiple={false}
                accept="application/msword, application/pdf"
                draggable
                action="#"
                maxPreviewFileSize={2097152}
                autoUpload={false}
                onChange={(files) => this.setState({ files })}
              >
                <div
                  className={`tg-uploader-cv-maker-btn ${
                    filesState.length > 0 ? 'd-none' : ''
                  }`}
                >
                  <VStack spacing={24} alignItems="center">
                    <VStack.Item>
                      <img
                        src="choose-cv-upload-icon.png"
                        alt="own-cvmaker-logo"
                      />
                    </VStack.Item>
                    <VStack.Item>
                      <b>
                        Drag & drop or <a href="#">choose file </a>to upload
                      </b>
                    </VStack.Item>
                    <VStack.Item>
                      <div className="d-grid">
                        <small>PDF or DOCX</small>
                        <br />
                        <div className="m-auto">Max: 10MB</div>
                      </div>
                    </VStack.Item>
                  </VStack>
                </div>
              </Uploader>
              {filesState.length > 0 && (
                <Message className="mt-4" type="warning" bordered showIcon>
                  <strong>Disclaimer:</strong> Some information may have been
                  parsed incorrectly by the AI. Please review the parsed values
                  in the form fields later.
                </Message>
              )}
            </Stack.Item>
          </Stack>
          <Stack
            spacing={16}
            className="w-80"
            justifyContent="flex-end"
            direction="row"
          >
            <Stack.Item>
              <Button
                onClick={this.onCancelClicked}
                style={{ display: filesState.length > 0 ? 'none' : '' }}
                appearance="subtle"
              >
                {t(constant.translation.general.formCancelButtonKey)}
              </Button>
            </Stack.Item>
            <Stack.Item>
              <Button
                className="next-btn"
                appearance="primary"
                onClick={this.onEnhanceClicked}
                disabled={filesState.length < 1}
              >
                <Icon as={FaMagic} />
                {t(constant.translation.general.formEnhanceButtonKey)}
              </Button>
            </Stack.Item>
          </Stack>
        </VStack>
      </FlexboxGrid>
    );
  }

  renderMainPage(): React.ReactNode {
    const { t } = this.props;
    const { cvMakerType } = this.state;

    return (
      <FlexboxGrid
        className="cv-maker-container"
        justify="center"
        align="middle"
      >
        <VStack spacing={36}>
          <Stack spacing={36} className="w-100">
            <Stack.Item className="w-100 tg-text-center">
              <HeadingGroup className="tg-cvmaker-heading">
                <Heading>{t(constant.translation.cvMaker.title)}</Heading>
                <Text muted>{t(constant.translation.cvMaker.subtitle)}</Text>
              </HeadingGroup>{' '}
            </Stack.Item>
          </Stack>
          <Stack spacing={36}>
            <Panel
              {...(cvMakerType === 'own' ? { className: 'selected' } : null)}
              bordered
              onClick={() => this.setState({ cvMakerType: 'own' })}
            >
              <div>
                <VStack spacing={16}>
                  <VStack.Item>
                    <img src="choose-cv-maker-own.png" alt="own-cvmaker-logo" />
                  </VStack.Item>
                  <VStack.Item>
                    <HeadingGroup>
                      <Heading>
                        {t(constant.translation.cvMaker.cvOwnTitle)}
                      </Heading>
                      <Text muted>
                        {t(constant.translation.cvMaker.cvOwnText)}
                      </Text>
                    </HeadingGroup>{' '}
                  </VStack.Item>
                </VStack>
              </div>
            </Panel>
            <Panel
              bordered
              {...(cvMakerType === 'build' ? { className: 'selected' } : null)}
              onClick={() => this.setState({ cvMakerType: 'build' })}
            >
              <div>
                <VStack spacing={16}>
                  <VStack.Item>
                    <img
                      src="choose-cv-maker-builder.png"
                      alt="own-cvmaker-logo"
                    />
                  </VStack.Item>
                  <VStack.Item>
                    <HeadingGroup>
                      <Heading>
                        {t(constant.translation.cvMaker.cvBuilderTitle)}
                      </Heading>
                      <Text muted>
                        {t(constant.translation.cvMaker.cvBuilderText)}
                      </Text>
                    </HeadingGroup>
                  </VStack.Item>
                </VStack>
              </div>
            </Panel>
          </Stack>
          <Stack
            spacing={16}
            className="w-100"
            justifyContent="flex-end"
            direction="row"
          >
            <Stack.Item>
              <Button
                appearance="subtle"
                disabled={!cvMakerType}
                onClick={() => this.setState({ cvMakerType: '' })}
              >
                {t(constant.translation.general.formCancelButtonKey)}
              </Button>
            </Stack.Item>
            <Stack.Item>
              <Button
                className="next-btn"
                appearance="primary"
                onClick={this.onNextClicked}
                disabled={!cvMakerType}
              >
                {t(constant.translation.general.formNextButtonKey)}
                <Icon as={FaArrowRight} />
              </Button>
            </Stack.Item>
          </Stack>
        </VStack>
      </FlexboxGrid>
    );
  }

  render(): React.ReactNode | null {
    const { step } = this.state;
    const isAuthenticated = AuthStorage.getInstance().isAuthenticated();

    switch (step) {
      case 1:
        return this.renderMainPage();
      case 2:
        if (isAuthenticated) {
          return <Navigate to="/cv-maker/build" />;
        }
        return <PrivateRoute />;
      case 3:
        if (isAuthenticated) {
          return this.renderCVUploader();
        }
        return <PrivateRoute />;
      default:
        return this.renderInprogressCVParsing();
    }
  }
}

export default withRouter(withTranslation()(CVMakerHomeScreen));
